import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  BILLING_CALCULATIONS,
  OFFLINE_PAYMENT,
  UTILITY_CONSUMER,
} from "tagTypes";

const useUtilityConsumerQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/utility-consumer",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useUtilityConsumerContactInfoQuery = () =>
  useGetQuery({
    endpoint: "/utility-consumer/contact-information",
    tags: [UTILITY_CONSUMER],
  });

export const useGetUtilityConsumerApplicationsQuery = (utilityConsumerId) =>
  useGetQuery({
    endpoint: `/utility-consumer/utility-consumer-application/${utilityConsumerId}`,
    tags: [UTILITY_CONSUMER],
  });

export const useGetUtilityConsumerDetailByConsumerIdQuery = (
  consumerId,
  query = {}
) =>
  useGetQuery({
    endpoint: `/power/utility-consumer/${consumerId}`,
    query,
    tags: [UTILITY_CONSUMER],
  });

export const useLazyGetBillDetailsWithConsumerNumber = () => {
  const [getBillDetails, data] = useLazyGetQuery();
  const getConsumerBillDetails = async (subscriptionType, query = {}) =>
    await getBillDetails({
      endpoint: `/subscription-type/${subscriptionType}/unpaid-bills`,
      query,
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [getConsumerBillDetails, data];
};

export const useLazyGetApplicationNumbers = () => {
  const [getBillDetails, data] = useLazyGetQuery();
  const getConsumerBillDetails = async (subscriptionType, query = {}) =>
    await getBillDetails({
      endpoint: `/subscription-type/${subscriptionType}/unpaid-demand-estimate-bills`,
      query,
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [getConsumerBillDetails, data];
};

export const useLazyGetConsumerNumberByGroupId = () => {
  const [getConsumerNumber, data] = useLazyGetQuery();
  const getCOnsumerNumberById = async (query = {}) =>
    await getConsumerNumber({
      endpoint: "/consumer-number",
      query,
    }).unwrap();
  return [getCOnsumerNumberById, data];
};

const useLazyUtilityConsumerQuery = () => {
  const [getUtilityConsumer, data] = useLazyGetQuery();
  const utilityConsumer = async (consumerId, query = {}) =>
    await getUtilityConsumer(
      {
        endpoint: `/power/utility-consumer/${consumerId}`,
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [utilityConsumer, data];
};

const useGetMeterAssignByUtilityConsumerId = (utilityConsumerId) =>
  useGetQuery({
    endpoint: `/utility-consumer/${utilityConsumerId}/meter-assign-tracking`,
    tags: [UTILITY_CONSUMER],
  });

export const useGetUtilityConsumerLoadTrackingDetails = (utilityConsumerId) =>
  useGetQuery({
    endpoint: `/utility-consumer/${utilityConsumerId}/consumer-details-tracking`,
    tags: [UTILITY_CONSUMER],
  });

const useGetConsumerCategoryWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-category-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerCategoryWiseQuery = () => {
  const [getConsumerCategory, data] = useLazyGetQuery();
  const consumerCategory = async (query = {}) =>
    await getConsumerCategory(
      {
        endpoint: "/report/consumer-category-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [consumerCategory, data];
};

const useGetConsumerTariffWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-tariff-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerTariffWiseWiseQuery = () => {
  const [getConsumerTariff, data] = useLazyGetQuery();
  const ConsumerTariff = async (query = {}) =>
    await getConsumerTariff(
      {
        endpoint: "/report/consumer-tariff-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [ConsumerTariff, data];
};

const useGetConsumerActivatedDisconnectedCategoryWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-activated-disconnected-category-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerActivatedDisconnectedCategoryWiseQuery = () => {
  const [getConsumerActivatedDisconnectedCategoryWise, data] =
    useLazyGetQuery();
  const consumerActivatedDisconnectedCategoryWise = async (query = {}) =>
    await getConsumerActivatedDisconnectedCategoryWise(
      {
        endpoint: "/report/consumer-activated-disconnected-category-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [consumerActivatedDisconnectedCategoryWise, data];
};

const useGetConsumerActivatedDisconnectedTariffWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-activated-disconnected-Tariff-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerActivatedDisconnectedTariffWiseQuery = () => {
  const [getConsumerActivatedDisconnectedTariffWise, data] = useLazyGetQuery();
  const fetchConsumerActivatedDisconnectedTariffWise = async (query = {}) =>
    await getConsumerActivatedDisconnectedTariffWise(
      {
        endpoint: "/report/consumer-activated-disconnected-Tariff-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [fetchConsumerActivatedDisconnectedTariffWise, data];
};

const useGetSopConsumerCategoryQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/sop-gerc",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazySopConsumerCategoryQuery = () => {
  const [getSopConsumerCategory, data] = useLazyGetQuery();
  const fetchSopConsumerCategoryWise = async (query = {}) =>
    await getSopConsumerCategory(
      {
        endpoint: "/report/sop-gerc",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [fetchSopConsumerCategoryWise, data];
};

const usePutUtilityConsumerMutation = () => {
  const [utilityConsumer, data] = usePutMutation();
  const addUtilityConsumer = async (payload, query = {}) =>
    await utilityConsumer({
      endpoint: "/power/utility-consumer",
      query,
      payload,
      toastMessage: "Utility consumer updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [addUtilityConsumer, data];
};

export const usePutUpdateOtherUtilityConsumerMutation = () => {
  const [utilityConsumer, data] = usePutMutation();
  const updateUtilityConsumer = async (payload, query = {}) =>
    await utilityConsumer({
      endpoint: "/other-utility-consumer",
      query,
      payload,
      toastMessage: "Utility consumer updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [updateUtilityConsumer, data];
};

export const usePutDisconnectUtilityConsumerPowerStatusMutation = () => {
  const [disconnectUtilityConsumerPower, data] = usePutMutation();
  const updatePowerStatus = async (payload, query = {}) =>
    await disconnectUtilityConsumerPower({
      endpoint: "utility-consumer/temporary-disconnect-connection",
      query,
      payload,
      toastMessage: "Status updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [updatePowerStatus, data];
};

export const usePutActivateConsumerPowerStatusMutation = () => {
  const [activateUtilityConsumerPower, data] = usePutMutation();
  const updatePowerStatus = async (payload, query = {}) =>
    await activateUtilityConsumerPower({
      endpoint: "utility-consumer/activate-connection",
      query,
      payload,
      toastMessage: "Status updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [updatePowerStatus, data];
};

const useLazyUtilityConsumerValidateQuery = () => {
  const [validateConsumer, data] = useLazyGetQuery();

  const consumerStatus = async (customerNumber, customerName, query = {}) =>
    await validateConsumer({
      endpoint: `/${customerNumber}/${customerName}/utility-consumer`,
      query,
    }).unwrap();
  return [consumerStatus, data];
};

export const usePostDisconnectedConsumerPaymentMutation = () => {
  const [dispatchBills, data] = usePostMutation();
  const setBillsPdf = async (payload) =>
    await dispatchBills({
      endpoint:
        "utility-consumer/temporary-disconnected-connection/payment-url",
      payload,
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [setBillsPdf, data];
};

export const usePostOfflinePaymentDetailsMutation = () => {
  const [addPaymentDetails, data] = usePostMutation();
  const postPaymentDetails = async (payload) =>
    await addPaymentDetails({
      endpoint: "/payment-transaction/offline-payment-collection",
      payload,
      toastMessage: "Payment completed successfully",
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [postPaymentDetails, data];
};

export const usePostDemandEstimateOfflinePaymentDetailsMutation = () => {
  const [addPaymentDetails, data] = usePostMutation();
  const postPaymentDetails = async (payload) =>
    await addPaymentDetails({
      endpoint:
        "/payment-transaction/demand-estimate-offline-payment-collection",
      payload,
      toastMessage: "Payment completed successfully",
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [postPaymentDetails, data];
};

export const usePostAddRefundDetailsMutation = () => {
  const [addRefundDetails, data] = usePostMutation();
  const postRefundDetails = async (utilityConsumerId, payload) =>
    await addRefundDetails({
      endpoint: `/utility-consumer/${utilityConsumerId}/consumer-refund-completion`,
      payload,
      toastMessage: "Consumer disconnection process completed successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [postRefundDetails, data];
};

export const usePostUtilityConsumerDisconnectionPaymentDetailsMutation = () => {
  const [addPaymentDetails, data] = usePostMutation();
  const postPaymentDetails = async (payload) =>
    await addPaymentDetails({
      endpoint:
        "/payment-transaction/demand-estimate-offline-payment-collection",
      payload,
      toastMessage: "Payment completed successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [postPaymentDetails, data];
};

const usePostSendMailMutation = () => {
  const [sendMail, data] = usePostMutation();
  const setSendMail = async (payload) =>
    await sendMail({
      endpoint: "/mail/send-mail",
      payload: payload || {},
      toastMessage: "Email Sent successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [setSendMail, data];
};

export {
  useUtilityConsumerQuery,
  useGetConsumerCategoryWiseQuery,
  useGetConsumerTariffWiseQuery,
  useGetConsumerActivatedDisconnectedCategoryWiseQuery,
  useGetConsumerActivatedDisconnectedTariffWiseQuery,
  useGetSopConsumerCategoryQuery,
  useLazyUtilityConsumerQuery,
  usePutUtilityConsumerMutation,
  useLazyUtilityConsumerValidateQuery,
  useGetLazyConsumerCategoryWiseQuery,
  useGetLazyConsumerTariffWiseWiseQuery,
  useGetLazyConsumerActivatedDisconnectedCategoryWiseQuery,
  useGetLazySopConsumerCategoryQuery,
  useGetLazyConsumerActivatedDisconnectedTariffWiseQuery,
  useGetMeterAssignByUtilityConsumerId,
  useUtilityConsumerContactInfoQuery,
  usePostSendMailMutation,
};
